import { CircleCheck, OctagonX, TriangleAlert } from "lucide-react";
import type { ReadinessLevel } from "../types";

const classes: Record<ReadinessLevel, string> = {
  RED: "border-danger-border bg-danger-bg text-danger",
  AMBER: "border-warning-border bg-warning-bg text-warning",
  GREEN: "border-success-border bg-success-bg text-success",
};

const glyphClasses: Record<ReadinessLevel, string> = {
  RED: "text-danger",
  AMBER: "text-warning",
  GREEN: "text-success",
};

const solidClasses: Record<ReadinessLevel, string> = {
  RED: "border-danger bg-[var(--gradient-danger)] text-white shadow-[var(--shadow-engraved)]",
  AMBER: "border-warning bg-[var(--gradient-warning)] text-white shadow-[var(--shadow-engraved)]",
  GREEN: "border-success bg-[var(--gradient-success)] text-white shadow-[var(--shadow-engraved)]",
};

const sizes = {
  xs: "px-2 py-[2px] text-[0.6875rem]",
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-xs",
  lg: "px-3 py-1 text-sm",
  xl: "px-3.5 py-1.5 text-sm",
};

export function StatusBadge({
  level,
  label,
  size = "md",
  tone = "soft",
  dotPosition = "leading",
}: {
  level: ReadinessLevel;
  label?: string;
  size?: keyof typeof sizes;
  tone?: "soft" | "solid" | "glyph";
  dotPosition?: "leading" | "trailing";
}) {
  const content = label ?? level;
  const Glyph = level === "GREEN" ? CircleCheck : level === "AMBER" ? TriangleAlert : OctagonX;
  const dot = (
    <span
      aria-hidden="true"
      className={`relative h-2 w-2 rounded-full bg-current ${level === "RED" ? "pulse-ring" : ""}`}
    />
  );

  const badgeClass =
    tone === "glyph"
      ? `inline-flex items-center gap-1.5 font-semibold text-tabular ${glyphClasses[level]} ${sizes[size]}`
      : `inline-flex items-center gap-1.5 rounded-pill border font-semibold text-tabular ${sizes[size]} ${
          tone === "solid" ? solidClasses[level] : classes[level]
        }`;

  return (
    <span className={badgeClass}>
      {tone === "glyph" ? (
        <Glyph aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.7} />
      ) : dotPosition === "leading" ? (
        dot
      ) : null}
      {content}
      {tone !== "glyph" && dotPosition === "trailing" ? dot : null}
    </span>
  );
}
